/**
 * Custom JavaScript for Shopify Store
 *
 * This file contains various customizations and enhancements for the store functionality.
 *
 * Event hooks available:
 * - document.addEventListener('variant:changed', function(event) {...})
 *   Triggered when a product variant changes, with variant details in event.detail.variant
 *
 * - document.addEventListener('product:added', function(event) {...})
 *   Triggered when a product is added to cart, with variant and quantity in event.detail
 *
 * You can also trigger custom events:
 * - document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *     bubbles: true,
 *     detail: { quantity: 1 }
 *   }));
 *
 * - document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *     bubbles: true
 *   }));
 */

// =============================================================================
// Environment Configuration
// =============================================================================
window.__leadConfig = {
  isProd: true, // SET TO false FOR STAGING
  // Temporarily disable Shopify /contact submissions during testing
  disableShopifyContact: true,
  zapierUrlProd: 'https://hooks.zapier.com/hooks/catch/5398638/2xcqua5/',
  zapierUrlStaging: 'https://hooks.zapier.com/hooks/catch/21948283/uhrmxab/'
};

// =============================================================================
// Centralized VPN Context and Helpers
// =============================================================================
window.dataLayer = window.dataLayer || [];
window.__leadContext = { isVpn: false, vpnChecked: false };

// Get cookie domain for cross-subdomain support
function cookieDomain() {
  // Properly scope cookies across subdomains
  const h = location.hostname;
  if (h.endsWith('.myshopify.com')) return h; // Don't widen on myshopify
  const p = h.split('.');
  return p.length >= 2 ? `.${p.slice(-2).join('.')}` : h;
}

// Secure cookie management for VPN status
function setVpnCookie(isVpn) {
  const base = `path=/;SameSite=Lax;Secure;domain=${cookieDomain()}`;
  document.cookie = isVpn ? `is_vpn=1;Max-Age=1800;${base}` : `is_vpn=;Max-Age=0;${base}`;
}

// Ensure VPN detection is resolved before form submission
async function ensureVpnResolved() {
  if (window.__leadContext.vpnChecked) return;
  
  try {
    if (typeof VPNDetectionManager?.detectVPN === 'function') {
      await VPNDetectionManager.detectVPN();
    } else {
      throw new Error('VPNDetectionManager.detectVPN unavailable');
    }
  } catch (e) {
    console.warn('VPN check failed/unavailable; defaulting to VPN for safety', e);
    window.__leadContext.isVpn = true;  // Conservative: treat missing or error as VPN
    window.__leadContext.vpnChecked = true;
    setVpnCookie(true);
  }
}

// Safe dataLayer push wrapper
function safePush(obj) {
  window.dataLayer = window.dataLayer || [];
  const isVpn = !!window.__leadContext?.isVpn;
  if (isVpn && obj && 'event' in obj) return; // Block ALL events if VPN
  window.dataLayer.push(obj || {});
}

// Centralized form success handler - Universal redirect for all forms
function handleLeadSuccess({ formType, isVpn, redirectUrl = '/pages/thank-you-for-contacting-us', neutralUrl = '/pages/thank-you-vpn?noconv=1' }) {
  if (isVpn) {
    safePush({ is_vpn: true });
    setVpnCookie(true);
    // location.assign(neutralUrl); // Temporarily disabled for testing submissions
    console.log('[handleLeadSuccess] (VPN) Redirect suppressed to:', neutralUrl);
    return;
  }
  
  // Non-VPN: safe to track
  safePush({ 
    event: 'lead_submit', 
    form_type: formType, 
    is_vpn: false 
  });
  
  // Dispatch custom event for non-VPN only
  document.documentElement.dispatchEvent(new CustomEvent('form:success', { 
    bubbles: true, 
    detail: { formType, isVpn: false } 
  }));
  
  // location.assign(redirectUrl); // Temporarily disabled for testing submissions
  console.log('[handleLeadSuccess] Redirect suppressed to:', redirectUrl);
}

// Production-ready lead submission with beacon fallback
async function postLead(url, payload) {
  // Use explicit URL or environment-based config
  const target = url || (window.__leadConfig.isProd 
    ? window.__leadConfig.zapierUrlProd 
    : window.__leadConfig.zapierUrlStaging);
  
  const body = new URLSearchParams();
  for (const [k, v] of Object.entries(payload)) {
    body.set(k, typeof v === 'object' ? JSON.stringify(v) : String(v ?? ''));
  }
  
  try {
    const res = await fetch(target, { 
      method: 'POST', 
      body, 
      keepalive: true 
    });
    return !!res && (res.ok || res.status === 204);
  } catch {
    // Beacon fallback with URL-encoded format
    if (navigator.sendBeacon) {
      const blob = new Blob([body.toString()], { 
        type: 'application/x-www-form-urlencoded;charset=UTF-8' 
      });
      return navigator.sendBeacon(target, blob) || false;
    }
    return false;
  }
}

// Removed Shopify /contact submission; handled by API + webhook only

// Export to window for Liquid templates
window.postLead = postLead;
// window.submitShopifyContactFromPayload = submitShopifyContactFromPayload; // removed: handled by API + webhook

// =============================================================================
// Unified Form Submission Handler
// =============================================================================

// Double-submit protection helper
function disableOnce(btn) {
  if (!btn) return;
  btn.disabled = true;
  const originalText = btn.textContent;
  btn.textContent = 'Submitting...';
  setTimeout(() => {
    btn.disabled = false;
    btn.textContent = originalText;
  }, 3000);
}

// Unified submit handler for all lead forms
async function handleUnifiedSubmit(form, tag) {
  const btn = form.querySelector('[type="submit"]');
  if (btn) disableOnce(btn);
  
  const fd = new FormData(form);
  const payload = {};
  
  // Extract all fields
  for (const [key, value] of fd.entries()) {
    payload[key] = value;
  }
  
  // Normalize contact[field] to flat fields
  for (const [k, v] of fd.entries()) {
    const m = k.match(/^contact\[(.+?)\]$/);
    if (m) {
      payload[m[1]] = v;
      // Ensure critical fields are accessible
      if (m[1] === 'email') payload.email = v;
      if (m[1] === 'name') payload.name = v;
      if (m[1] === 'first_name') payload.first_name = v;
      if (m[1] === 'last_name') payload.last_name = v;
    }
  }
  
  // If we have first and last name but no name, create it
  if (!payload.name && (payload.first_name || payload.last_name)) {
    payload.name = [payload.first_name, payload.last_name].filter(Boolean).join(' ');
  }
  
  // Add correlation ID for tracking
  payload._rid = Math.random().toString(36).slice(2);
  
  // Add timestamp and metadata
  payload.timestamp = new Date().toISOString();
  payload.page_url = window.location.href;
  payload.referrer = document.referrer || '';
  payload.user_agent = navigator.userAgent;
  
  // Resolve VPN state
  await ensureVpnResolved();
  const isVpn = !!window.__leadContext?.isVpn;
  
  // Add VPN fields
  payload.isVpn = isVpn;
  payload.vpn_detected = isVpn;
  payload.vpn_status = window.__leadContext?.vpn_status || (isVpn ? 'detected' : 'not_detected');
  payload.user_ip = VPNDetectionManager?.userIP || IPAddressManager?.userIP || 'unknown';
  
  // Add VPN indicator for Zapier
  if (isVpn) {
    payload['(VPN)'] = '(VPN)';
  }
  
  // Tag subject if VPN detected
  const baseSubject = payload.subject || `${tag} Inquiry`;
  payload.subject = isVpn ? `${baseSubject} (VPN)` : baseSubject;
  
  console.log(`[${tag}] Payload:`, payload);
  
  // Submit to webhook only (main-style avoids Shopify /contact)
  const webhookOk = await postLead(null, payload);
  
  console.log(`[${tag}] Results - Webhook: ${webhookOk}`);
  
  // Redirect based on VPN status
  const redirectUrl = isVpn ? '/pages/thank-you-vpn?noconv=1' : '/pages/thank-you-for-contacting-us';
  window.location.href = redirectUrl;
}

// Wire form with proper event handling
function wireForm(form, tag) {
  if (!form || form.dataset.wired === 'true') return;
  form.dataset.wired = 'true';
  
  form.addEventListener('submit', async e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    
    try {
      await handleUnifiedSubmit(form, tag);
    } catch (error) {
      console.error(`[${tag}] Form submission error:`, error);
      alert('There was an error submitting the form. Please try again.');
      
      const btn = form.querySelector('[type="submit"]');
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Submit';
      }
    }
  }, { capture: true });
  
  console.log(`[${tag}] Form wired successfully`);
}

// QA bypass for testing (use in console: window.__vpnBypass = true)
if (window.__vpnBypass === true) { 
  window.__leadContext.isVpn = false; 
}

// Initialize product equipment form handler - using unified approach
function initProductEquipmentForm() {
  // The product inquiry form from new-form.liquid now handles VPN detection internally
  // through its submitFormToZapier function, so we don't need to wire it here
  console.log('Product inquiry form uses integrated VPN detection in new-form.liquid');
  
  // Check for the Shopify native equipment form (on equipment product pages)
  const equipmentForms = document.querySelectorAll('form.js-equipment-form');
  equipmentForms.forEach(form => {
    // Add product context to form data
    const product = window.theme?.product || {};
    
    // Add hidden fields for product info if not present
    if (!form.querySelector('[name="product_title"]')) {
      const titleInput = document.createElement('input');
      titleInput.type = 'hidden';
      titleInput.name = 'product_title';
      titleInput.value = product.title || document.querySelector('.product__title')?.textContent || 'Equipment';
      form.appendChild(titleInput);
    }
    
    if (!form.querySelector('[name="product_url"]')) {
      const urlInput = document.createElement('input');
      urlInput.type = 'hidden';
      urlInput.name = 'product_url';
      urlInput.value = window.location.href;
      form.appendChild(urlInput);
    }
    
    // Wire with unified handler
    wireForm(form, 'product_equipment');
  });
}

// Listen for Shopify section events
document.addEventListener('shopify:section:load', function(e) {
  if (e.target.querySelector('.form.js-equipment-form')) {
    initProductEquipmentForm();
  }
});

document.addEventListener('shopify:section:reorder', function(e) {
  initProductEquipmentForm();
});

// =============================================================================
// Main Initialization
// =============================================================================
document.addEventListener("DOMContentLoaded", function () {
    // Initialize core functionality
    initEquipmentRequestForm();
    initProductEquipmentForm();
    initCollectionListHeights();
    ReCaptchaManager.init();
  
    // Initialize contact page form
    ContactPageForm.init();
  
    // Initialize IP address detection manager
    IPAddressManager.init();
    
    // Product forms are wired in initProductEquipmentForm()
    // Do not wire contact or financing forms with unified handler
    
    // The contact modal (#contactForm) already has its own handler in contact-modal.liquid
    // Don't double-wire it here
  });
  
  // =============================================================================
  // IP Address Detection Manager v1.0 - START
  // =============================================================================
  const IPAddressManager = {
    userIP: null,
    isDetecting: false,
    detectionPromise: null,
  
    init: function () {
      // Start IP detection early but don't block page load
      this.getUserIP();
    },
  
    getUserIP: async function () {
      // Return cached IP if already detected
      if (this.userIP) {
        return this.userIP;
      }
  
      // Return existing promise if detection is in progress
      if (this.isDetecting && this.detectionPromise) {
        return this.detectionPromise;
      }
  
      this.isDetecting = true;
  
      // Create and store the promise
      this.detectionPromise = this._detectIP();
  
      try {
        const ip = await this.detectionPromise;
        this.userIP = ip;
        this.isDetecting = false;
        return ip;
      } catch (error) {
        console.warn('Could not detect IP address:', error);
        this.isDetecting = false;
        return 'unknown';
      }
    },
  
    _detectIP: async function () {
      // Try multiple services for reliability
      const services = [
        'https://api.ipify.org?format=json',
        'https://ipapi.co/json/',
        'https://ip-api.com/json/?fields=query'
      ];
  
      for (const service of services) {
        try {
          const response = await fetch(service, {
            timeout: 5000,
            mode: 'cors'
          });
  
          if (!response.ok) continue;
  
          const data = await response.json();
  
          // Extract IP from different service response formats
          const ip = data.ip || data.query;
  
          if (ip && this._isValidIP(ip)) {
            console.log('IP address detected:', ip);
            return ip;
          }
        } catch (error) {
          console.warn(`IP service ${service} failed:`, error);
          continue;
        }
      }
  
      throw new Error('All IP detection services failed');
    },
  
    _isValidIP: function (ip) {
      // Basic IP validation (IPv4 and IPv6)
      const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
  
      return ipv4Regex.test(ip) || ipv6Regex.test(ip);
    }
  };
  
  // Make IPAddressManager globally available
  window.IPAddressManager = IPAddressManager;
  
  // =============================================================================
  // IP Address Detection Manager v1.0 - END
  // =============================================================================
  
  // =============================================================================
  // Equipment Request Form
  // =============================================================================
  function initEquipmentRequestForm() {
    const btnRequestForm = document.querySelector(".js-btn-request-form");
  
    if (!btnRequestForm) return;
  
    btnRequestForm.addEventListener("click", function (e) {
      e.preventDefault();
  
      // Find all equipment forms
      const equipmentForms = document.querySelectorAll(".js-equipment-form");
  
      // Simulate click on delivery tab
      simulateButtonClick(".js-tabs-1__bar_button--delivery");
  
      // Scroll to and focus on the first visible form
      equipmentForms.forEach((elem) => {
        if (!isElementHidden(elem)) {
          scrollToElement(elem);
          focusFirstInput(elem);
        }
      });
    });
  }
  
  // Helper functions for Equipment Request Form
  function isElementHidden(el) {
    return el.offsetParent === null;
  }
  
  function simulateButtonClick(buttonSelector) {
    const button = document.querySelector(buttonSelector);
    if (button) button.click();
  }
  
  function scrollToElement(element) {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  
  function focusFirstInput(element) {
    setTimeout(() => {
      const firstInput = element.querySelector('input[type="text"]');
      if (firstInput) firstInput.focus();
    }, 500);
  }
  
  // =============================================================================
  // Collection List Heights
  // =============================================================================
  function initCollectionListHeights() {
    const collectionListSections = document.querySelectorAll(
      ".collection-list-2-section"
    );
  
    if (!collectionListSections.length) return;
  
    collectionListSections.forEach((collection) => {
      const collectionList = collection.querySelector(".collection-list");
      if (!collectionList) return;
  
      const listHeight = collectionList.clientHeight;
      const collectionItems = collection.querySelectorAll(".collection-item");
  
      // Set uniform height for all items
      collectionItems.forEach((item) => {
        item.style.height = `${listHeight}px`;
      });
    });
  }
  
  // =============================================================================
  // PLANNED FEATURES - NOT ACTIVE (Kept for future implementation)
  // =============================================================================
  
  /*
   * =============================================================================
   * Serial Number Request - FEATURE NOT ACTIVE
   * =============================================================================
   * This feature would intercept "Add to Cart" clicks and request serial numbers
   * for products that require them.
   *
   * Disabled code preserved for future implementation.
   */
  document.addEventListener('DOMContentLoaded', function() {
    // Intercept "Add to Cart" button clicks - use capture phase to ensure we're first
    document.addEventListener('click', function(event) {
      // More specifically target add-to-cart buttons within product forms
      const button = event.target.closest(
        'form[action="/cart/add"] button[type="submit"], ' + 
        'form[action="/cart/add"] button[name="add"], ' +
        'form.product-form button[type="submit"], ' +
        'button[data-action="add-to-cart"]'
      );
      
      if (button) {
        // Find the specific cart/add form this button belongs to
        const form = button.closest('form[action="/cart/add"], form.product-form');
        
        if (form) {
          const productId = form.querySelector('input[name="id"]')?.value;
          
          if (productId) {
            // Check if this product requires serial
            const requiresSerial = document.querySelector(`#product-metafield-${productId}-requires-serial`) 
              ? document.querySelector(`#product-metafield-${productId}-requires-serial`).value === 'true'
              : false;
            
            if (requiresSerial) {
              // IMPORTANT - Stop the event immediately - prevent default and stop propagation
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
              
              // Show the serial number modal
              showSerialNumberModal(form);
              
              return false;
            }
          }
        }
      }
    }, true); // Use 'true' for capture phase to intercept before other handlers
    
    // Function to display machine information modal
    function showSerialNumberModal(form) {
      // Create modal if it doesn't exist
      if (!document.getElementById('serial-number-modal')) {
        const modal = document.createElement('div');
        modal.id = 'serial-number-modal';
        modal.className = 'serial-modal';
        modal.innerHTML = `
          <div class="serial-modal-content">
            <span class="serial-modal-close">&times;</span>
            <h3>Machine Information Required</h3>
            <p>Machine Make, Model and Serial Number is required for this Part.</p>
            <div class="serial-input-container">
              <label for="product-serial-number">Machine Make, Model and Serial Number:</label>
              <input type="text" id="product-serial-number" placeholder="Enter machine make, model and serial number">
            </div>
            <div class="serial-modal-buttons">
              <button id="submit-with-serial" class="serial-btn serial-btn-primary">Continue with Machine Information</button>
              <button id="bypass-serial" class="serial-btn serial-btn-secondary">Continue without Machine Information</button>
            </div>
          </div>
        `;
        document.body.appendChild(modal);
        
        // Add styles for modal
        const style = document.createElement('style');
        style.setAttribute('data-id', 'serial-modal-styles');
        style.textContent = `
          .serial-modal {
            display: none;
            position: fixed;
            z-index: 9999;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(2px);
          }
          .serial-modal-content {
            background-color: white;
            margin: 10% auto;
            padding: 32px;
            border-radius: 8px;
            width: 90%;
            max-width: 540px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
            position: relative;
          }
          .serial-modal-content h3 {
            margin: 0 0 16px 0;
            font-size: 22px;
            font-weight: 600;
            color: #1a1a1a;
            padding-right: 30px;
            line-height: 1.3;
          }
          .serial-modal-content p {
            margin: 0 0 24px 0;
            font-size: 15px;
            color: #4a4a4a;
            line-height: 1.5;
          }
          .serial-modal-close {
            position: absolute;
            top: 24px;
            right: 24px;
            cursor: pointer;
            font-size: 32px;
            line-height: 1;
            color: #999;
            transition: color 0.2s;
            font-weight: 300;
          }
          .serial-modal-close:hover {
            color: #333;
          }
          .serial-input-container {
            margin: 0 0 24px 0;
          }
          .serial-input-container label {
            display: block;
            margin-bottom: 8px;
            font-size: 14px;
            font-weight: 600;
            color: #333;
          }
          .serial-input-container input {
            width: 100%;
            padding: 12px 16px;
            font-size: 15px;
            border: 2px solid #ddd;
            border-radius: 6px;
            box-sizing: border-box;
            transition: border-color 0.2s;
          }
          .serial-input-container input:focus {
            outline: none;
            border-color: #28427A;
          }
          .serial-input-container input::placeholder {
            color: #999;
          }
          .serial-modal-buttons {
            display: flex;
            gap: 12px;
            margin-top: 28px;
          }
          .serial-btn {
            flex: 1;
            padding: 14px 20px;
            font-size: 15px;
            font-weight: 600;
            border-radius: 6px;
            cursor: pointer;
            border: none;
            transition: all 0.2s;
          }
          .serial-btn-primary {
            background-color: #28427A;
            color: white;
          }
          .serial-btn-primary:hover {
            background-color: #1e3158;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(40, 66, 122, 0.3);
          }
          .serial-btn-secondary {
            background-color: #f5f5f5;
            color: #666;
            border: 2px solid #ddd;
          }
          .serial-btn-secondary:hover {
            background-color: #e8e8e8;
            color: #333;
          }
          .cart-item-serial-required {
            display: inline-block;
            background-color: #FFF4E5;
            color: #D48806;
            padding: 2px 8px;
            border-radius: 3px;
            font-size: 12px;
            margin-left: 5px;
          }
          .add-serial-btn {
            padding: 6px 12px;
            background-color: #28427A;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            margin: 5px 0;
          }
          @media (max-width: 600px) {
            .serial-modal-content {
              margin: 20% auto;
              padding: 24px;
              width: 95%;
            }
            .serial-modal-content h3 {
              font-size: 19px;
            }
            .serial-modal-buttons {
              flex-direction: column;
            }
          }
          @keyframes serialPulse {
            0% { box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.4); }
            70% { box-shadow: 0 0 0 6px rgba(255, 152, 0, 0); }
            100% { box-shadow: 0 0 0 0 rgba(255, 152, 0, 0); }
          }
        `;
        document.head.appendChild(style);
        
        // Handle modal close
        document.querySelector('.serial-modal-close').addEventListener('click', function() {
          document.getElementById('serial-number-modal').style.display = 'none';
        });
      }
      
      // Get product details to display in modal
      let productName = 'This product';
      
      // Try to get the product name from different possible locations
      // First try Quick View modal context
      const quickViewModal = form.closest('.quickview-modal, .quick-view-modal, [data-section-type="quick-view"]');
      if (quickViewModal) {
        const nameElement = quickViewModal.querySelector('.product-title, .product__title, h1, h2');
        if (nameElement) {
          productName = nameElement.textContent.trim();
        }
      } else {
        // Then try regular product page
        const nameElement = document.querySelector('.product-title, .product__title, h1.title');
        if (nameElement) {
          productName = nameElement.textContent.trim();
        }
      }
      
      document.querySelector('#serial-number-modal h3').textContent = `Machine Information Required: ${productName}`;
      
      // Show the modal
      document.getElementById('serial-number-modal').style.display = 'block';
      
      // Handle submit with serial number
      const submitBtn = document.getElementById('submit-with-serial');
      
      // Remove old event listener if exists
      const newSubmitBtn = submitBtn.cloneNode(true);
      submitBtn.parentNode.replaceChild(newSubmitBtn, submitBtn);
      
      newSubmitBtn.addEventListener('click', function() {
        const serialNumber = document.getElementById('product-serial-number').value;
        
        if (!serialNumber.trim()) {
          alert('Please enter machine make, model and serial number');
          return;
        }
        
        // Clean up any existing property inputs first
        const existingInputs = form.querySelectorAll('input[name^="properties"]');
        existingInputs.forEach(input => {
          if (input.name === 'properties[Machine Info]' || input.name === 'properties[Machine Info Required]') {
            input.remove();
          }
        });
        
        // Add machine information as a line item property
        const serialInput = document.createElement('input');
        serialInput.type = 'hidden';
        serialInput.name = 'properties[Machine Info]';
        serialInput.value = serialNumber;
        form.appendChild(serialInput);
        
        // Submit the form
        form.submit();
        document.getElementById('serial-number-modal').style.display = 'none';
      });
      
      // Handle bypass option
      const bypassBtn = document.getElementById('bypass-serial');
      
      // Remove old event listener if exists
      const newBypassBtn = bypassBtn.cloneNode(true);
      bypassBtn.parentNode.replaceChild(newBypassBtn, bypassBtn);
      
      newBypassBtn.addEventListener('click', function() {
        // Clean up any existing property inputs first
        const existingInputs = form.querySelectorAll('input[name^="properties"]');
        existingInputs.forEach(input => {
          if (input.name === 'properties[Machine Info]' || input.name === 'properties[Machine Info Required]') {
            input.remove();
          }
        });
        
        // Add a flag to indicate machine info was bypassed
        const bypassInput = document.createElement('input');
        bypassInput.type = 'hidden';
        bypassInput.name = 'properties[Machine Info Required]';
        bypassInput.value = 'Yes - Not Provided';
        form.appendChild(bypassInput);
        
        // Submit the form
        form.submit();
        document.getElementById('serial-number-modal').style.display = 'none';
      });
    }
  });
  
  /*
   * =============================================================================
   * Stop Search Iframe on Focus - FEATURE NOT ACTIVE
   * =============================================================================
   * This would prevent Doofinder's overlay from triggering on search input focus
   * until the user starts typing.
   *
   * Disabled code preserved for future implementation.
   */
  /*
  document.addEventListener('DOMContentLoaded', () => {
    const searchEl = document.querySelector('.search-bar__input');
  
    if (searchEl) {
      console.log('Search input found');
      let interactionStarted = false;
  
      // This function blocks the Doofinder focus-triggered overlay
      const blockFocus = (e) => {
        if (!interactionStarted) {
          e.stopImmediatePropagation();
          console.log('Blocked Doofinder overlay on focus');
        }
      };
  
      // Attach focus listener in capture mode so we block it before Doofinder sees it
      searchEl.addEventListener('focus', blockFocus, true);
  
      // On any character input, consider it an interaction
      searchEl.addEventListener('keydown', (e) => {
        console.log('Search input keydown event');
        if (!interactionStarted && e.key.length === 1) {
          console.log('Search input character input confirmed');
  
          // Allow focus to work from now on
          interactionStarted = true;
          searchEl.removeEventListener('focus', blockFocus, true);
  
          // Focus is already active — Doofinder will pick up the keypress and act
          // No need to blur/focus or manually dispatch anything
        }
      });
    }
  });
  */
  
  /*
   * =============================================================================
   * Location-Based Dynamic Pricing - FEATURE NOT ACTIVE
   * =============================================================================
   * This section is planned for future implementation.
   * Code will be developed when the feature is ready to be implemented.
   */
  
  // =============================================================================
  // ReCAPTCHA Manager
  // =============================================================================
  const ReCaptchaManager = {
    scriptLoaded: false,
    renderedCaptchas: new Set(),
    widgetIds: new Map(), // Track widget IDs for each container
  
    init: function () {
      this.loadScript();
    },
  
    loadScript: function () {
      if (
        this.scriptLoaded ||
        document.querySelector('script[src*="recaptcha"]')
      ) {
        this.scriptLoaded = true;
        return;
      }
  
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.scriptLoaded = true;
        console.log("reCAPTCHA script loaded successfully");
      };
      document.head.appendChild(script);
    },
  
    clearContainer: function (containerId) {
      // Clear tracking for a specific container - useful when DOM is being regenerated
      this.renderedCaptchas.delete(containerId);
      this.widgetIds.delete(containerId);
      console.log(`Cleared reCAPTCHA tracking for: ${containerId}`);
    },
  
    renderCaptcha: function (containerId, errorId) {
  const container = document.getElementById(containerId);
  const errorElement = document.getElementById(errorId);

  if (!container) {
    console.error(`reCAPTCHA container not found: ${containerId}`);
    return false;
  }

  // Check if already rendered
  const existingIframe = container.querySelector('iframe[src*="recaptcha"]');
  if (existingIframe) {
    console.log(`reCAPTCHA already exists for: ${containerId}`);
    return true;
  }

  let attemptCount = 0;
  const maxAttempts = 10; // Maximum retry attempts

  const attemptRender = () => {
    attemptCount++;

    if (
      typeof grecaptcha !== "undefined" &&
      typeof grecaptcha.render === "function"
    ) {
      try {
        // Check if container already has a reCAPTCHA widget (double-check before render)
        const checkIframe = container.querySelector('iframe[src*="recaptcha"]');
        if (checkIframe) {
          console.log(`reCAPTCHA already rendered for: ${containerId}`);
          if (errorElement) {
            errorElement.style.display = "none";
          }
          return true;
        }

        // Attempt to render
        const widgetId = grecaptcha.render(container, {
          sitekey: "6LdtgPQqAAAAAEkslzcJ6caAt-FtU6gM5FNMKJyi",
        });

        this.renderedCaptchas.add(containerId);
        this.widgetIds.set(containerId, widgetId);
        console.log(`reCAPTCHA rendered successfully for: ${containerId} with widget ID: ${widgetId}`);

        // Hide error if it was showing
        if (errorElement) {
          errorElement.style.display = "none";
        }

        return true;
      } catch (error) {
        console.warn(`reCAPTCHA render attempt ${attemptCount} failed for ${containerId}:`, error);
        
        // Retry if we haven't exceeded max attempts
        if (attemptCount < maxAttempts) {
          setTimeout(attemptRender, 500);
        } else {
          // Only show error after all attempts are exhausted
          console.error(`reCAPTCHA rendering failed after ${maxAttempts} attempts`);
          if (errorElement) {
            errorElement.textContent =
              "Could not load reCAPTCHA. Please refresh the page.";
            errorElement.style.display = "block";
          }
        }
        return false;
      }
    } else {
      // Script not ready yet, retry if we haven't exceeded max attempts
      if (attemptCount < maxAttempts) {
        setTimeout(attemptRender, 500);
      } else {
        console.error(`reCAPTCHA script not loaded after ${maxAttempts} attempts`);
        if (errorElement) {
          errorElement.textContent =
            "Could not load reCAPTCHA. Please refresh the page.";
          errorElement.style.display = "block";
        }
      }
    }
  };

  attemptRender();
  return true;
},
  
    attemptToMapExistingWidget: function (containerId, container) {
      // Try to find the widget ID for an existing iframe
      if (
        typeof grecaptcha !== "undefined" &&
        typeof grecaptcha.getResponse === "function"
      ) {
        // Get all existing reCAPTCHA iframes on the page
        const allRecaptchaIframes = document.querySelectorAll(
          'iframe[src*="recaptcha"]'
        );
        const allContainers = [];
  
        // Find all containers that have reCAPTCHA iframes
        allRecaptchaIframes.forEach((iframe) => {
          const containerWithRecaptcha = iframe.closest(".g-recaptcha");
          if (containerWithRecaptcha && containerWithRecaptcha.id) {
            allContainers.push(containerWithRecaptcha.id);
          }
        });
  
        console.log(`Found reCAPTCHA containers: ${allContainers.join(", ")}`);
  
        // Assign widget IDs based on DOM order
        allContainers.forEach((containerId, index) => {
          if (!this.widgetIds.has(containerId)) {
            // Try to verify this widget ID actually works
            try {
              grecaptcha.getResponse(index); // This will throw if widget doesn't exist
              this.widgetIds.set(containerId, index);
              console.log(
                `Mapped widget ID ${index} to container: ${containerId}`
              );
            } catch (e) {
              console.warn(
                `Widget ID ${index} not valid for container: ${containerId}`
              );
            }
          }
        });
      }
    },
  
      validateCaptcha: function (errorId, containerId) {
    const errorElement = document.getElementById(errorId);
    console.log(`Validating reCAPTCHA for container: ${containerId}`);

    if (typeof grecaptcha !== "undefined") {
      try {
        let response = null;

        // Simple approach: Try to get response from any available widget
        // This is more reliable than complex widget tracking
        for (let i = 0; i < 10; i++) {
          try {
            const testResponse = grecaptcha.getResponse(i);
            if (testResponse && testResponse.length > 0) {
              response = testResponse;
              console.log(`Found valid reCAPTCHA response from widget ${i}`);
              break;
            }
          } catch (e) {
            // Continue to next widget
          }
        }

        // If no response found, try the default method
        if (!response) {
          try {
            response = grecaptcha.getResponse();
            if (response && response.length > 0) {
              console.log(`Found valid reCAPTCHA response from default widget`);
            }
          } catch (e) {
            console.log('Default reCAPTCHA response failed');
          }
        }

        if (!response || response.length === 0) {
          if (errorElement) {
            errorElement.style.display = "block";
          }
          console.log(`reCAPTCHA validation failed - no valid response found`);
          return false;
        }

        if (errorElement) {
          errorElement.style.display = "none";
        }
        console.log(`reCAPTCHA validation successful`);
        return response;
      } catch (error) {
        console.error("reCAPTCHA validation error:", error);
        if (errorElement) {
          errorElement.style.display = "block";
        }
        return false;
      }
    }

    console.warn("reCAPTCHA not available for validation");
    return true; // Allow submission if reCAPTCHA isn't available
  },
  
    resetCaptcha: function (containerId) {
      if (typeof grecaptcha !== "undefined") {
        try {
          let widgetId;
          
          // Priority 1: Use data attribute from container
          if (containerId) {
            const container = document.getElementById(containerId);
            if (container) {
              const storedWidgetId = container.getAttribute('data-widget-id');
              if (storedWidgetId !== null) {
                widgetId = parseInt(storedWidgetId);
              }
            }
          }
          
          // Priority 2: Use tracked widget ID
          if (widgetId === undefined && containerId && this.widgetIds.has(containerId)) {
            widgetId = this.widgetIds.get(containerId);
          }
          
          if (widgetId !== undefined) {
            // Reset specific widget
            grecaptcha.reset(widgetId);
            console.log(
              `Reset reCAPTCHA for container: ${containerId}, widget: ${widgetId}`
            );
          } else {
            // Reset all widgets as fallback
            grecaptcha.reset();
            console.log("Reset all reCAPTCHAs (fallback)");
          }
  
          // Remove from tracking since it's been reset
          if (containerId) {
            this.renderedCaptchas.delete(containerId);
            this.widgetIds.delete(containerId);
            // Clear data attribute
            const container = document.getElementById(containerId);
            if (container) {
              container.removeAttribute('data-widget-id');
            }
          }
        } catch (e) {
          console.log("reCAPTCHA reset error:", e);
        }
      }
    },
  
    resetAllCaptchas: function () {
      if (typeof grecaptcha !== "undefined") {
        try {
          grecaptcha.reset();
          // Clear all tracking
          this.renderedCaptchas.clear();
          this.widgetIds.clear();
          console.log("Reset all reCAPTCHAs");
        } catch (e) {
          console.log("reCAPTCHA reset error:", e);
        }
      }
    },
  };
  
  // Make ReCaptchaManager globally available
  window.ReCaptchaManager = ReCaptchaManager;
  
  // =============================================================================
  // Contact Page Form Injection
  // =============================================================================
  const ContactPageForm = {
    init: function() {
      // Check if we're on the contact-us page
      if (window.location.pathname.includes('/pages/contact-us') || window.location.pathname.includes('/pages/contact')) {
        this.injectContactForm();
      }
    },
  
    injectContactForm: async function() {
      const targetContainer = document.getElementById('contact-form');
        if (!targetContainer) {
          console.log('Contact form container not found');
          return;
        }
  
      // Get UTM parameters
      const utmData = getUtmParams();
      
      // Get device and browser information
      const deviceType = this.getDeviceType();
      const browserName = this.getBrowserName();
  
      // No IP pre-fetching - will be resolved on-demand during form submit
  
      // Create the form HTML
      const formHTML = `
        <div class="contact-page-form">
          <form id="pageContactForm" class="contact-form" onsubmit="handlePageContactFormSubmit(event)">
            <div class="contact-form-row">
            <div class="contact-form-field">
              <label class="field-label">Full Name</label>
              <input type="text" name="full_name" required>
            </div>
              <div class="contact-form-field">
                <label class="field-label">Email *</label>
                <input type="email" name="email" required>
              </div>
            </div>
  
            <div class="contact-form-row">
              <div class="contact-form-field phone-field">
                <label class="field-label">Phone *</label>
                <div class="phone-input-container">
                  <select name="country_code" class="country-code-select">
                    <option value="+1">+1</option>
                  </select>
                  <input type="tel" name="phone" required>
                </div>
              </div>
            </div>
  
            <div class="contact-form-row">
              <div class="contact-form-field full-width">
                <label class="field-label">Your Message</label>
                <textarea
                  name="body"
                  rows="4"
                  required
                ></textarea>
              </div>
            </div>
  
            <div class="contact-method-section">
              <label class="section-label">Preferred method of contact *</label>
              <div class="contact-method-options">
                <label class="radio-option">
                  <input type="radio" name="preferred_contact" value="email" required>
                  <span class="radio-text">Email</span>
                </label>
                <label class="radio-option">
                  <input type="radio" name="preferred_contact" value="call" required>
                  <span class="radio-text">Call</span>
                </label>
                <label class="radio-option">
                  <input type="radio" name="preferred_contact" value="sms" required>
                  <span class="radio-text">SMS</span>
                </label>
                <label class="radio-option">
                  <input type="radio" name="preferred_contact" value="no_preference" required>
                  <span class="radio-text">No Preference</span>
                </label>
              </div>
            </div>
  
            <div class="recaptcha-container">
              <div
                id="page-contact-recaptcha"
                class="g-recaptcha"
                data-sitekey="6LdtgPQqAAAAAEkslzcJ6caAt-FtU6gM5FNMKJyi"
              ></div>
              <span id="page-contact-recaptcha-error" style="color: red; display: none; font-size: 12px;"
                >Please complete the reCAPTCHA.</span
              >
            </div>
  
            <!-- Hidden fields -->
            <input type="hidden" name="product_title" value="General Contact">
            <input type="hidden" name="product_url" value="">
            <input type="hidden" name="form_type" value="contact_inquiry">
            <input type="hidden" name="contact[utm_source]" value="${utmData.utm_source || ''}">
            <input type="hidden" name="contact[utm_medium]" value="${utmData.utm_medium || ''}">
            <input type="hidden" name="contact[utm_campaign]" value="${utmData.utm_campaign || ''}">
            <input type="hidden" name="contact[gclid]" value="${utmData.gclid || ''}">
            <input type="hidden" name="contact[gbraid]" value="${utmData.gbraid || ''}">
            <input type="hidden" name="contact[wbraid]" value="${utmData.wbraid || ''}">
            <input type="hidden" name="contact[device]" value="${deviceType}">
            <input type="hidden" name="contact[browser]" value="${browserName}">
            <input type="hidden" name="contact[ip_address]" id="contact-ip" value="">
  
            <button type="submit" class="contact-submit-btn">Submit</button>
          </form>
        </div>
      `;
  
      // Add the CSS styles
      this.addContactFormStyles();
  
      // Inject the form
      targetContainer.innerHTML = formHTML;
  
      // Initialize reCAPTCHA with longer delay to ensure script is fully loaded
      setTimeout(() => {
        if (typeof ReCaptchaManager !== 'undefined') {
          ReCaptchaManager.renderCaptcha('page-contact-recaptcha', 'page-contact-recaptcha-error');
        }
      }, 1500);
    },
  
    getDeviceType: function() {
      const userAgent = navigator.userAgent.toLowerCase();
      if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
        return 'Tablet';
      }
      if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) {
        return 'Mobile';
      }
      return 'Desktop';
    },
  
    getBrowserName: function() {
      const userAgent = navigator.userAgent;
      let browserName = 'Unknown';
      
      if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
        browserName = 'Opera';
      } else if (userAgent.indexOf('Edg') > -1) {
        browserName = 'Edge';
      } else if (userAgent.indexOf('Chrome') > -1) {
        browserName = 'Chrome';
      } else if (userAgent.indexOf('Safari') > -1) {
        browserName = 'Safari';
      } else if (userAgent.indexOf('Firefox') > -1) {
        browserName = 'Firefox';
      } else if (userAgent.indexOf('Trident') > -1) {
        browserName = 'Internet Explorer';
      }
      
      return browserName;
    },
  
    addContactFormStyles: function() {
      // Only add styles if they don't exist yet
      if (!document.getElementById('contact-page-form-styles')) {
        const styleElement = document.createElement('style');
        styleElement.id = 'contact-page-form-styles';
        styleElement.textContent = `
          .contact-page-form {
            max-width: 600px;
            margin: 0 auto;
            padding: 30px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          }
  
          .contact-form-header {
            margin-bottom: 30px;
          }
  
          .contact-form-title {
            font-size: 28px;
            margin: 0 0 10px 0;
            color: #333;
            font-weight: 600;
          }
  
          .contact-form-header p {
            color: #666;
            margin: 0;
            font-size: 16px;
          }
  
          .contact-form-row {
            display: flex;
            gap: 15px;
            margin-bottom: 15px;
          }
  
          .contact-form-field {
            flex: 1;
          }
  
          .contact-form-field.full-width {
            flex: none;
            width: 100%;
          }
  
          .field-label {
            display: block;
            font-size: 14px;
            color: #333;
            margin-bottom: 5px;
            font-weight: 500;
            text-align: start;
          }
  
          .contact-form-field input,
          .contact-form-field select,
          .contact-form-field textarea {
            width: 100%;
            padding: 12px 15px;
            border: 1px solid #ddd;
            border-radius: 5px;
            font-size: 14px;
            box-sizing: border-box;
            font-family: inherit;
          }
  
          .contact-form-field textarea {
            resize: vertical;
            min-height: 80px;
          }
  
          .contact-form-field input:focus,
          .contact-form-field select:focus,
          .contact-form-field textarea:focus {
            outline: none;
            border-color: #2c5aa0;
          }
  
          .phone-input-container {
            display: flex;
            gap: 5px;
          }
  
          .country-code-select {
            width: 80px;
            flex-shrink: 0;
          }
  
          .phone-input-container select {
            flex: 1;
          }
  
          .phone-input-container input {
            flex: 3;
          }
  
          .contact-form-checkbox {
            margin: 15px 0 20px;
          }
  
          .contact-form-checkbox label {
            display: flex;
            align-items: center;
            font-size: 14px;
            color: #666;
            cursor: pointer;
          }
  
          .contact-form-checkbox input[type='checkbox'] {
            margin-right: 8px;
          }
  
          .contact-method-section {
            margin: 20px 0;
          }
  
          .section-label {
            display: block;
            font-size: 14px;
            color: #333;
            margin-bottom: 10px;
            font-weight: 500;
          }
  
          .contact-method-options {
            display: flex;
            flex-wrap: wrap;
            gap: 15px;
          }
  
          .radio-option {
            display: flex;
            align-items: center;
            cursor: pointer;
            font-size: 14px;
            color: #666;
          }
  
          .radio-option input[type='radio'] {
            margin-right: 6px;
            margin-top: 0;
          }
  
          .recaptcha-container {
            margin: 20px 0;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
          }
  
          .contact-submit-btn {
            width: 100%;
            background-color: #ff3333;
            color: white;
            border: none;
            padding: 15px;
            border-radius: 5px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            margin-top: 10px;
          }
  
          .contact-submit-btn:hover {
            background-color: #8a3a37;
          }
  
          .contact-submit-btn:disabled {
            background-color: #ccc;
            cursor: not-allowed;
          }
  
          /* Mobile Responsive */
          @media (max-width: 768px) {
            .contact-page-form {
              padding: 20px;
              margin: 10px;
            }
  
            .contact-form-row {
              flex-direction: column;
              gap: 10px;
            }
  
            .contact-form-title {
              font-size: 24px;
            }
  
            .contact-method-options {
              flex-direction: column;
              gap: 10px;
            }
  
            .phone-input-container {
              flex-direction: row;
            }
  
            .country-code-select {
              width: 70px;
            }
          }
        `;
        document.head.appendChild(styleElement);
      }
    }
  };
  
  // =============================================================================
  // Contact Page Form Handler Functions
  // =============================================================================
  
  // UTM Parameter Handler (reuse from modal)
  function getUtmParams() {
    // Extended to capture Google Ads attribution parameters alongside core UTMs
    const KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'gclid', 'gbraid', 'wbraid'];
    const urlParams = new URLSearchParams(window.location.search);
    const result = {};
  
    KEYS.forEach((key) => {
      if (urlParams.has(key)) {
        const val = urlParams.get(key);
        if (val) {
          result[key] = val;
          sessionStorage.setItem(key, val);
        } else {
          sessionStorage.removeItem(key);
        }
      } else {
        const storedVal = sessionStorage.getItem(key);
        if (storedVal) {
          result[key] = storedVal;
        }
      }
    });
  
    return result;
  }
  
  // API submission function (reuse from modal)
  async function submitFormToAPI(payload) {
    try {
      // Ensure VPN detection runs before API submission
      if (typeof window.ensureVpnResolved === 'function') {
        await window.ensureVpnResolved();
      }
      const shopDomain = Shopify.shop;
      const apiUrl = '/apps/leads?shop=' + encodeURIComponent(shopDomain);
      
      console.log("Submitting to URL:", apiUrl);

      const isVpnValue = typeof payload.isVpn !== 'undefined' ? !!payload.isVpn : !!(window.__leadContext && window.__leadContext.isVpn);
      const vpnStatusValue = payload.vpn_status || (isVpnValue ? 'detected' : 'not_detected');

      const apiPayload = {
        form_type: payload.form_type,
        first_name: payload.first_name,
        last_name: payload.last_name,
        email: payload.email,
        phone: payload.phone,
        page_url: window.location.href,
        utf8: "✓",
        utm_source: payload.utm_source || '',
        utm_medium: payload.utm_medium || '',
        utm_campaign: payload.utm_campaign || '',
        // Extended attribution parameters
        gclid: payload.gclid || '',
        gbraid: payload.gbraid || '',
        wbraid: payload.wbraid || '',
        body: payload.body || 'No description provided',
        preferred_contact: payload.preferred_contact,
        device: payload.device || '',
        browser: payload.browser || '',
        ip_address: payload.ip_address || '',
        isVpn: isVpnValue,
        vpn_detected: isVpnValue,
        vpn_detected_text: String(isVpnValue),
        vpn_status: vpnStatusValue,
      };
  
      console.log("Sending to API:", apiPayload);
  
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(apiPayload),
      });
  
      const responseText = await response.text();
      console.log("Raw response:", responseText);
  
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        console.error("Failed to parse response as JSON:", e);
        throw new Error("Invalid response from server");
      }
  
      if (response.ok && result.success) {
        console.log("Lead created successfully:", result.leadId);
      } else {
        throw new Error(result.error || "Failed to submit form");
      }
    } catch (error) {
      console.error("Error submitting form to API:", error);
      throw error;
    }
  }
  
  // Page contact form submit handler (main-style: submit to app endpoint + Zapier)
  async function handlePageContactFormSubmit(event) {
    event.preventDefault();
  
    const form = event.target;
    const submitButton = form.querySelector('.contact-submit-btn');
  
    console.log('Page contact form submitted');
  
    // Validate reCAPTCHA
    const recaptchaResponse = ReCaptchaManager
      ? ReCaptchaManager.validateCaptcha('page-contact-recaptcha-error', 'page-contact-recaptcha')
      : true;
  
    console.log('reCAPTCHA response:', recaptchaResponse);
  
    if (!recaptchaResponse) {
      console.log('reCAPTCHA validation failed');
      return;
    }
  
    // Disable submit button
    submitButton.disabled = true;
    submitButton.textContent = 'Submitting...';

    // Ensure VPN detection is resolved
    await ensureVpnResolved();
    const isVpn = !!window.__leadContext?.isVpn;
    const vpnStatus = isVpn ? 'detected' : 'not_detected';

    try {
      // Prepare form data
      const formData = new FormData(form);
      const payload = {};
  
      // Convert FormData to object, handling contact[] format
      for (let [key, value] of formData.entries()) {
        const match = key.match(/contact\[(.*?)\]/);
        if (match && match[1]) {
          payload[match[1]] = value;
        } else {
          payload[key] = value;
        }
      }
  
      // Get UTM parameters (they should already be in payload from form fields)
      const utmData = getUtmParams();
      payload.utm_source = payload.utm_source || utmData.utm_source || '';
      payload.utm_medium = payload.utm_medium || utmData.utm_medium || '';
      payload.utm_campaign = payload.utm_campaign || utmData.utm_campaign || '';
  
      // Split full_name into first_name and last_name
      if (payload.full_name) {
        const nameParts = payload.full_name.trim().split(/\s+/);
        if (nameParts.length === 1) {
          payload.first_name = nameParts[0];
          payload.last_name = '';
        } else if (nameParts.length >= 2) {
          payload.first_name = nameParts[0];
          payload.last_name = nameParts.slice(1).join(' ');
        }
        delete payload.full_name;
      }
  
      // Combine country code and phone number
      if (payload.country_code && payload.phone) {
        payload.phone = payload.country_code + ' ' + payload.phone;
        delete payload.country_code;
      }
  
      // Add timestamp and additional info
      payload.timestamp = new Date().toISOString();
      payload.page_url = window.location.href;
      payload.referrer = document.referrer || '';
      payload.user_agent = navigator.userAgent;
      payload.subject = `Contact Inquiry - ${payload.product_title || 'General Inquiry'}`;
      payload.form_source = 'contact_page';
  
      // Add reCAPTCHA response if available
      if (typeof recaptchaResponse === 'string') {
        payload.recaptcha_response = recaptchaResponse;
      }

      // VPN fields  
      payload.isVpn = isVpn;
      payload.vpn_detected = isVpn;
      payload.vpn_detected_text = String(isVpn);
      payload.vpn_status = vpnStatus;
      
      // Add VPN indicator for Zapier
      if (isVpn) {
        payload['(VPN)'] = '(VPN)';
      }
      
      // Ensure IP address is properly set
      const detectedIP = VPNDetectionManager?.userIP || IPAddressManager?.userIP || payload['ip_address'] || 'unknown';
      payload.ip_address = detectedIP;
      payload.user_ip = detectedIP;

      console.log('Page contact form payload:', payload);
      console.log('UTM data extracted:', {
        utm_source: payload.utm_source,
        utm_medium: payload.utm_medium,
        utm_campaign: payload.utm_campaign,
        device: payload.device,
        browser: payload.browser
      });
  
      // Submit to API
      await submitFormToAPI(payload);
  
      // Submit to Zapier (use env-configured URL)
      const zapierWebhookUrl = window.__leadConfig && window.__leadConfig.isProd
        ? window.__leadConfig.zapierUrlProd
        : window.__leadConfig.zapierUrlStaging;
  
      console.log('Sending to Zapier:', payload);
  
      await fetch(zapierWebhookUrl, {
        method: 'POST',
        body: JSON.stringify(payload),
        mode: 'no-cors',
      });
  
      console.log('Page contact form sent to Zapier');
      // Redirect based on VPN status
      const redirectUrl = isVpn ? '/pages/thank-you-vpn?noconv=1' : '/pages/thank-you-for-contacting-us';
      window.location.href = redirectUrl;
      
    } catch (error) {
      console.error('Page contact form error:', error);
      alert('There was an error submitting the form. Please try again.');
      submitButton.disabled = false;
      submitButton.textContent = 'Submit';
      if (typeof ReCaptchaManager !== 'undefined') {
        ReCaptchaManager.resetCaptcha('page-contact-recaptcha');
      }
    }
  }
  
  // LEGACY CODE BELOW - KEPT FOR REFERENCE BUT NOT EXECUTED
  async function handlePageContactFormSubmit_LEGACY(event) {
    event.preventDefault();
  
    const form = event.target;
    const submitButton = form.querySelector('.contact-submit-btn');
  
    console.log('Page contact form submitted');
  
    // Validate reCAPTCHA
    const recaptchaResponse = ReCaptchaManager
      ? ReCaptchaManager.validateCaptcha('page-contact-recaptcha-error', 'page-contact-recaptcha')
      : true;
  
    console.log('reCAPTCHA response:', recaptchaResponse);
  
    if (!recaptchaResponse) {
      console.log('reCAPTCHA validation failed');
      return;
    }
  
    // Disable submit button
    submitButton.disabled = true;
    submitButton.textContent = 'Submitting...';

    // Ensure VPN detection is resolved
    await ensureVpnResolved();
    const isVpn = !!window.__leadContext?.isVpn;
    const vpnStatus = isVpn ? 'detected' : 'not_detected';

    // Resolve IP on-demand and mirror into the hidden input (optional but keeps form consistent)
    try {
      const ip = await IPAddressManager.getUserIP();
      const ipInput = form.querySelector('#contact-ip');
      if (ipInput) ipInput.value = ip;
    } catch (e) {
      // ignore; we'll fill user_ip later from manager or 'unknown'
    }
  
    try {
      // Prepare form data
      const formData = new FormData(form);
      const payload = {};
  
      // Convert FormData to object, handling contact[] format
      for (let [key, value] of formData.entries()) {
        const match = key.match(/contact\[(.*?)\]/);
        if (match && match[1]) {
          // Extract the field name from contact[fieldname] format
          payload[match[1]] = value;
        } else {
          payload[key] = value;
        }
      }
  
      // Get UTM parameters (they should already be in payload from form fields)
      const utmData = getUtmParams();
      payload.utm_source = payload.utm_source || utmData.utm_source || '';
      payload.utm_medium = payload.utm_medium || utmData.utm_medium || '';
      payload.utm_campaign = payload.utm_campaign || utmData.utm_campaign || '';
  
      // Split full_name into first_name and last_name
      if (payload.full_name) {
        const nameParts = payload.full_name.trim().split(/\s+/);
        if (nameParts.length === 1) {
          payload.first_name = nameParts[0];
          payload.last_name = '';
        } else if (nameParts.length >= 2) {
          payload.first_name = nameParts[0];
          payload.last_name = nameParts.slice(1).join(' ');
        }
        delete payload.full_name;
      }
  
      // Combine country code and phone number
      if (payload.country_code && payload.phone) {
        payload.phone = payload.country_code + ' ' + payload.phone;
        delete payload.country_code;
      }
  
      // Add timestamp and additional info
      payload.timestamp = new Date().toISOString();
      payload.page_url = window.location.href;
      payload.referrer = document.referrer || '';
      payload.user_agent = navigator.userAgent;
      // Subject will be set after VPN detection
      payload.form_source = 'contact_page';
  
      // Add reCAPTCHA response if available
      if (typeof recaptchaResponse === 'string') {
        payload.recaptcha_response = recaptchaResponse;
      }
  
      // VPN fields  
      payload.isVpn = isVpn;                              // New field name for consistency
      payload.vpn_detected = isVpn;                       // Keep for backward compatibility
      payload.vpn_detected_text = String(isVpn);          // 'true'/'false' for text filters
      payload.vpn_status = vpnStatus;
      
      // Ensure IP address is properly set
      const detectedIP = VPNDetectionManager?.userIP || IPAddressManager?.userIP || payload['ip_address'] || 'unknown';
      payload.ip_address = detectedIP;
      payload.user_ip = detectedIP;

      // Subject tagging
      const baseSubject = `Contact Inquiry - ${payload.product_title || 'General Inquiry'}`;
      payload.subject = isVpn ? `${baseSubject} (VPN)` : baseSubject;

    console.log('Page contact form payload:', payload);
      console.log('UTM data extracted:', {
        utm_source: payload.utm_source,
        utm_medium: payload.utm_medium,
        utm_campaign: payload.utm_campaign,
        device: payload.device,
        browser: payload.browser
      });
  
      // Submit to API
      await submitFormToAPI(payload);
  
      // Submit to Zapier
      // const zapierWebhookUrl = 'https://hooks.zapier.com/hooks/catch/5398638/2xcqua5/'; // Production webhook - commented out
      const zapierWebhookUrl = 'https://hooks.zapier.com/hooks/catch/21948283/uhrmxab/'; // Staging webhook
  
      console.log('Sending to Zapier:', payload);
  
      await fetch(zapierWebhookUrl, {
        method: 'POST',
        body: JSON.stringify(payload),
        mode: 'no-cors',
      });
  
      console.log('Page contact form sent to Zapier');
      
      // Use centralized success handler
      handleLeadSuccess({
        formType: 'contact',
        isVpn: isVpn,
        redirectUrl: '/pages/thank-you-for-contacting-us',
        neutralUrl: '/pages/thank-you-vpn?noconv=1'
      });
      
    } catch (error) {
      console.error('Page contact form error:', error);
      alert('There was an error submitting the form. Please try again.');
      submitButton.disabled = false;
      submitButton.textContent = 'Submit';
      if (typeof ReCaptchaManager !== 'undefined') {
        ReCaptchaManager.resetCaptcha('page-contact-recaptcha');
      }
    }
  }
  
  
  /**
   * Hide Recommendation Images from Search Engine Crawlers
   * Prevents equipment recommendation images from appearing in Google search results
   * while keeping them visible to users after page load
   */
  
  (function() {
    'use strict';
    
    // Configuration
    const CONFIG = {
      // Product handles that should hide recommendation images
      targetProducts: [
        'jlg-part-8528074s-paint-skytrak-orange-gallon',
        'jlg-part-7026901-kit-seat-cushion',
        'light-led-ind-red-12v-nh-genie-part-824879gt-or-824879',
        'handle-knob-speed-cont-jlg-part-2560136',
        'controller-joystick-dual-axis-jlg-part-1600452'
        
      ],
      
      // Delay before showing images (milliseconds)
      showDelay: 2000,
      
      // Fallback delay (in case primary method fails)
      fallbackDelay: 5000,
      
      // Selectors
      selectors: {
        recommendationSection: '.product-recommendations',
        recommendationImages: '.product-recommendations img',
        imageWrappers: '.product-recommendations .product-item__image-wrapper'
      }
    };
    
    /**
     * Get current product handle from URL or meta tags
     */
    function getCurrentProductHandle() {
      // Method 1: Try to get from URL path
      const pathMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
      if (pathMatch) {
        return pathMatch[1];
      }
      
      // Method 2: Try to get from meta tags or global variables
      if (window.theme && window.theme.productHandle) {
        return window.theme.productHandle;
      }
      
      // Method 3: Try to get from canonical URL
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        const canonicalMatch = canonical.href.match(/\/products\/([^\/\?]+)/);
        if (canonicalMatch) {
          return canonicalMatch[1];
        }
      }
      
      return null;
    }
    
    /**
     * Check if current product should hide recommendation images
     */
    function shouldHideRecommendationImages() {
      const currentHandle = getCurrentProductHandle();
      return currentHandle && CONFIG.targetProducts.includes(currentHandle);
    }
    
    /**
     * Create and inject CSS to hide images
     */
    function injectHidingCSS() {
      const style = document.createElement('style');
      style.id = 'hide-recommendation-images-css';
      style.textContent = `
        ${CONFIG.selectors.recommendationImages} {
          display: none !important;
          visibility: hidden !important;
        }
        
        ${CONFIG.selectors.imageWrappers} {
          background: #f5f5f5 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          min-height: 200px !important;
          position: relative !important;
        }
        
        ${CONFIG.selectors.imageWrappers}:before {
          content: "Loading..." !important;
          color: #999 !important;
          font-size: 14px !important;
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          z-index: 1 !important;
        }
      `;
      
      document.head.appendChild(style);
      return style;
    }
    
    /**
     * Remove hiding CSS and show images
     */
    function showRecommendationImages() {
      // Remove the hiding CSS
      const hidingStyle = document.getElementById('hide-recommendation-images-css');
      if (hidingStyle) {
        hidingStyle.remove();
      }
      
      // Force show images with inline styles
      const images = document.querySelectorAll(CONFIG.selectors.recommendationImages);
      const wrappers = document.querySelectorAll(CONFIG.selectors.imageWrappers);
      
      images.forEach(img => {
        img.style.display = '';
        img.style.visibility = '';
        img.style.opacity = '1';
        img.style.transition = 'opacity 0.3s ease';
      });
      
      wrappers.forEach(wrapper => {
        wrapper.style.background = '';
        wrapper.style.minHeight = '';
        wrapper.style.display = '';
        wrapper.style.alignItems = '';
        wrapper.style.justifyContent = '';
        wrapper.style.position = '';
      });
      
      console.log('Recommendation images are now visible');
    }
    
    /**
     * Main initialization function
     */
    function init() {
      // Only run on product pages
      if (!window.location.pathname.includes('/products/')) {
        return;
      }
      
      // Check if this product should hide recommendation images
      if (!shouldHideRecommendationImages()) {
        return;
      }
      
      console.log('Hiding recommendation images for SEO purposes...');
      
      // Inject CSS to hide images immediately
      const hidingStyle = injectHidingCSS();
      
      // Set timeout to show images after delay
      setTimeout(() => {
        showRecommendationImages();
      }, CONFIG.showDelay);
      
      // Fallback timeout in case something goes wrong
      setTimeout(() => {
        if (document.getElementById('hide-recommendation-images-css')) {
          console.warn('Fallback: Showing recommendation images after extended delay');
          showRecommendationImages();
        }
      }, CONFIG.fallbackDelay);
      
      // Also show images when user interacts with the page
      const interactionEvents = ['click', 'scroll', 'touchstart', 'keydown'];
      let hasInteracted = false;
      
      const handleInteraction = () => {
        if (!hasInteracted && document.getElementById('hide-recommendation-images-css')) {
          hasInteracted = true;
          setTimeout(() => {
            showRecommendationImages();
          }, 500);
          
          // Remove event listeners
          interactionEvents.forEach(event => {
            document.removeEventListener(event, handleInteraction);
          });
        }
      };
      
      interactionEvents.forEach(event => {
        document.addEventListener(event, handleInteraction, { passive: true });
      });
    }
    
    /**
     * Wait for DOM to be ready
     */
    function domReady() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
      } else {
        init();
      }
    }
    
    // Start the script
    domReady();
    
    // Export for debugging (optional)
    window.hideRecommendationImages = {
      init: init,
      show: showRecommendationImages,
      getCurrentHandle: getCurrentProductHandle,
      config: CONFIG
    };
    
  })();

// =============================================================================
// VPN Detection Manager using VPNapi.io API
// =============================================================================
const VPNDetectionManager = {
  // Configuration
  config: {
    apiKey: 'e2776a42304f4f36b93bce7a848a0931', // VPNapi.io API key
    apiEndpoint: 'https://vpnapi.io/api/',
    checkOnPageLoad: false, // No page-load checks - only on-demand
    blockFormSubmission: false, // No blocking - silent tagging only
    testMode: false, // Production mode - silent tagging
    retryAttempts: 2,
    timeout: 5000
  },

  // State
  isVPN: null,
  isChecking: false,
  userIP: null,
  checkPromise: null,
  lastResponse: null,

  // Initialize VPN detection
  init: function() {
    console.log('VPN Detection Manager initialized with VPNapi.io (on-demand mode)');
    // No page-load IP detection or global interception - only on-demand when forms submit
  },


  // Main VPN detection function - integrated with centralized context
  detectVPN: async function() {
    // Return cached result from centralized context if available
    if (window.__leadContext.vpnChecked) {
      return window.__leadContext.isVpn;
    }

    // Return existing promise if check is in progress
    if (this.isChecking && this.checkPromise) {
      return this.checkPromise;
    }

    this.isChecking = true;

    // Create and store the promise
    this.checkPromise = this._performVPNCheck();

    try {
      const result = await this.checkPromise;
      this.isVPN = result;
      this.isChecking = false;
      
      // Update centralized context
      window.__leadContext.isVpn = result;
      window.__leadContext.vpnChecked = true;
      setVpnCookie(result);
      
      return result;
    } catch (error) {
      this.isChecking = false;
      
      // Conservative: treat errors as VPN
      console.warn('VPN detection error, defaulting to VPN', error);
      window.__leadContext.isVpn = true;
      window.__leadContext.vpnChecked = true;
      setVpnCookie(true);
      
      return true; // Return true (VPN) on error
    }
  },

  // Perform the actual VPN check
  _performVPNCheck: async function() {
    // Get user IP first
    if (!this.userIP) {
      this.userIP = await this._getUserIP();
    }

    const url = `${this.config.apiEndpoint}${this.userIP}?key=${this.config.apiKey}`;

    for (let attempt = 1; attempt <= this.config.retryAttempts; attempt++) {
      try {
        console.log(`VPN check attempt ${attempt} for IP: ${this.userIP}`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

        const response = await fetch(url, {
          method: 'GET',
          signal: controller.signal,
          headers: {
            'Accept': 'application/json'
          }
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        console.log('VPNapi.io Response:', data);

        // Store full response for debug modal
        this.lastResponse = data;

        // VPNapi.io response structure is different
        // Check if the response indicates a VPN
        const isVPN = data.security && (data.security.vpn === true || data.security.proxy === true || data.security.tor === true);

        console.log('VPN Detection Results:', {
          vpn: data.security?.vpn,
          proxy: data.security?.proxy,
          tor: data.security?.tor,
          isVPN: isVPN,
          country: data.location?.country,
          city: data.location?.city,
          isp: data.network?.name,
          risk: data.risk
        });

        return isVPN;

      } catch (error) {
        console.warn(`VPN check attempt ${attempt} failed:`, error);
        
        if (attempt === this.config.retryAttempts) {
          console.error('All VPN check attempts failed');
          // In production, allow form submission if VPN check fails
          // In test mode, return false to prevent false positives
          return false;
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }
    }
    
    return false; // Default to allowing if all attempts fail
  },

  // Get user IP address
  _getUserIP: async function() {
    // Try to use existing IP detection if available
    if (typeof IPAddressManager !== 'undefined') {
      try {
        const ip = await IPAddressManager.getUserIP();
        if (ip && ip !== 'unknown') {
          return ip;
        }
      } catch (error) {
        console.warn('Could not get IP from IPAddressManager:', error);
      }
    }

    // Fallback IP detection services
    const services = [
      'https://api.ipify.org?format=json',
      'https://ipapi.co/json/',
      'https://ip-api.com/json/?fields=query'
    ];

    for (const service of services) {
      try {
        const response = await fetch(service, { timeout: 3000 });
        if (!response.ok) continue;

        const data = await response.json();
        const ip = data.ip || data.query;

        if (ip && this._isValidIP(ip)) {
          return ip;
        }
      } catch (error) {
        console.warn(`IP service ${service} failed:`, error);
        continue;
      }
    }

    throw new Error('Could not detect IP address');
  },

  // Validate IP address format
  _isValidIP: function(ip) {
    const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
    return ipv4Regex.test(ip) || ipv6Regex.test(ip);
  },



  // Check if current page is a contact page
  _isContactPage: function() {
    const currentPath = window.location.pathname.toLowerCase();
    const contactPages = [
      '/pages/contact',
      '/pages/contact-us',
      '/contact',
      '/contact-us'
    ];
    
    return contactPages.some(page => currentPath.includes(page));
  },

  // Check if form is a contact form
  _isContactForm: function(form) {
    // Check form action
    if (form.action && form.action.includes('/contact')) {
      return true;
    }

    // Check form method and look for contact fields
    if (form.method && form.method.toLowerCase() === 'post') {
      const hasContactFields = form.querySelector('input[name*="contact"], textarea[name*="contact"], input[name="email"], textarea[name="body"]');
      if (hasContactFields) {
        return true;
      }
    }

    // Check for specific form IDs or classes
    const contactFormSelectors = [
      'contact-form',
      'pageContactForm',
      '.contact-form',
      '.form[action*="contact"]'
    ];

    return contactFormSelectors.some(selector => {
      if (selector.startsWith('.')) {
        return form.classList.contains(selector.slice(1));
      } else if (selector.startsWith('#')) {
        return form.id === selector.slice(1);
      } else {
        return form.id === selector || form.classList.contains(selector);
      }
    });
  },

  // Reset VPN detection state (for testing)
  reset: function() {
    this.isVPN = null;
    this.isChecking = false;
    this.userIP = null;
    this.checkPromise = null;
    console.log('VPN detection state reset');
  },

  // Set API key
  setApiKey: function(apiKey) {
    this.config.apiKey = apiKey;
    console.log('VPNapi.io API key configured');
  },

  // Enable/disable test mode
  setTestMode: function(enabled) {
    this.config.testMode = enabled;
    console.log('VPN test mode:', enabled ? 'enabled' : 'disabled');
  }
};

// Make VPNDetectionManager globally available
window.VPNDetectionManager = VPNDetectionManager;

// Initialize VPN detection on page load
document.addEventListener('DOMContentLoaded', function() {
  VPNDetectionManager.init();
});

// Export for debugging (minimal - no modal functions)
window.debugVPN = {
  manager: VPNDetectionManager,
  checkVPN: () => VPNDetectionManager.detectVPN(),
  reset: () => VPNDetectionManager.reset()
};


// PDP page - Trust banner tooltip
document.addEventListener("DOMContentLoaded", function () {
  const benefits = document.querySelectorAll(".product__benefit");
  let activeTooltip = null;

  benefits.forEach(benefit => {
    const tooltip = benefit.querySelector(".product__benefit-tooltip");
    if (!tooltip) return;

    tooltip.style.display = "none";

    // Close button inside tooltip
    const closeBtn = tooltip.querySelector("[close-benefit]");
    if (closeBtn) {
      closeBtn.addEventListener("click", (e) => {
        e.preventDefault();
        tooltip.style.display = "none";
        activeTooltip = null;
      });
    }

    // Show on mouse enter
    benefit.addEventListener("mouseenter", () => {
      if (activeTooltip && activeTooltip !== tooltip) {
        activeTooltip.style.display = "none";
      }
      tooltip.style.display = "block";
      activeTooltip = tooltip;
    });

    // Hide when leaving the benefit OR the tooltip
    benefit.addEventListener("mouseleave", () => {
      tooltip.style.display = "none";
      if (activeTooltip === tooltip) activeTooltip = null;
    });

    tooltip.addEventListener("mouseleave", () => {
      tooltip.style.display = "none";
      if (activeTooltip === tooltip) activeTooltip = null;
    });
  });
});
